import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  detectManualPwaInstallKind,
  isPwaListedAsInstalledRelatedApp,
  isRunningAsInstalledPwa,
  shouldShowNonChromeInstallHint,
  subscribeInstalledPwaDisplayMode,
  type ManualPwaInstallKind,
} from '../utils/pwaInstallEnvironment';
import {
  clearDeferredInstallPrompt,
  ensurePwaInstallOfferCapture,
  getDeferredInstallPrompt,
  markPwaInstalled,
  subscribePwaInstallOffer,
  type BeforeInstallPromptEvent,
} from '../utils/pwaInstallOffer';

export function usePwaInstallOffer() {
  const [installed, setInstalled] = useState(() =>
    typeof window === 'undefined' ? false : isRunningAsInstalledPwa()
  );
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(() =>
    typeof window === 'undefined' ? null : getDeferredInstallPrompt()
  );
  const [isInstalling, setIsInstalling] = useState(false);
  const [waitedForPrompt, setWaitedForPrompt] = useState(false);

  useEffect(() => {
    ensurePwaInstallOfferCapture();
    const sync = () => {
      setDeferredPrompt(getDeferredInstallPrompt());
      setInstalled(isRunningAsInstalledPwa());
    };
    sync();
    return subscribePwaInstallOffer(sync);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const refresh = () => setInstalled(isRunningAsInstalledPwa());
    refresh();
    const unsubscribe = subscribeInstalledPwaDisplayMode(refresh);
    void isPwaListedAsInstalledRelatedApp().then((relatedInstalled) => {
      if (cancelled || !relatedInstalled) return;
      markPwaInstalled();
      setInstalled(true);
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (installed || deferredPrompt || detectManualPwaInstallKind()) {
      setWaitedForPrompt(true);
      return undefined;
    }
    const timer = window.setTimeout(() => setWaitedForPrompt(true), 1600);
    return () => window.clearTimeout(timer);
  }, [installed, deferredPrompt]);

  const manualKind: ManualPwaInstallKind | null = useMemo(() => {
    if (installed || deferredPrompt) return null;
    const detected = detectManualPwaInstallKind();
    if (detected) return detected;
    return waitedForPrompt ? 'generic' : null;
  }, [installed, deferredPrompt, waitedForPrompt]);

  const canNativeInstall = Boolean(deferredPrompt) && !installed;
  const showNonChromeInstallHint = !installed && shouldShowNonChromeInstallHint();

  const promptNativeInstall = useCallback(async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
    const prompt = getDeferredInstallPrompt();
    if (!prompt) return 'unavailable';
    setIsInstalling(true);
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      clearDeferredInstallPrompt();
      if (choice.outcome === 'accepted') {
        markPwaInstalled();
        setInstalled(isRunningAsInstalledPwa());
        return 'accepted';
      }
      return 'dismissed';
    } finally {
      setIsInstalling(false);
    }
  }, []);

  return {
    installed,
    deferredPrompt,
    manualKind,
    canNativeInstall,
    showNonChromeInstallHint,
    isInstalling,
    promptNativeInstall,
  };
}
